import React, { useEffect } from 'react';

export function LoadingState({ label = 'Cargando…' }) {
  return (
    <div
      className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-muted"
      role="status"
      aria-live="polite"
    >
      <span className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-primary" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({ title, description }) {
  return (
    <div className="rounded-lg border border-dashed border-line bg-surface px-4 py-12 text-center">
      <p className="text-base font-semibold text-ink">{title}</p>
      {description && <p className="mt-1 text-sm text-muted">{description}</p>}
    </div>
  );
}

export function InlineAlert({ tone = 'info', children, className = '' }) {
  const tones = {
    info: 'border-primary/30 bg-primary/5 text-primary',
    error: 'border-danger/40 bg-danger/10 text-danger',
    success: 'border-success/40 bg-success/10 text-success',
    warning: 'border-warning/40 bg-warning/10 text-warning',
  };
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${tones[tone] || tones.info} ${className}`} role="alert">
      {children}
    </div>
  );
}

export function Toast({ open, message, tone = 'success', onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => onClose?.(), 3200);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] max-w-sm">
      <InlineAlert tone={tone} className="shadow-lg">
        <div className="flex items-start gap-3">
          <span className="flex-1">{message}</span>
          <button type="button" className="btn-admin-ghost !min-h-8 !px-2" onClick={onClose} aria-label="Cerrar">
            ✕
          </button>
        </div>
      </InlineAlert>
    </div>
  );
}

export function PageHeader({ title, onBack, action }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        {onBack && (
          <button type="button" className="btn-admin-ghost !min-h-10 !px-2" onClick={onBack} aria-label="Volver">
            ←
          </button>
        )}
        <h1 className="admin-title">{title}</h1>
      </div>
      {action}
    </div>
  );
}

export function Modal({ open, title, onClose, children, footer, size = 'md' }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div className="dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`dialog-panel ${widths[size] || widths.md} max-h-[90vh] overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-lg font-semibold text-ink">{title}</h2>
          <button type="button" className="btn-admin-ghost !min-h-9 !px-2" onClick={onClose} aria-label="Cerrar">
            ✕
          </button>
        </div>
        <div className="max-h-[calc(90vh-8rem)] overflow-y-auto px-4 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-line px-4 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title = 'Confirmar',
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  danger = false,
  loading = false,
  onConfirm,
  onClose,
}) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={loading ? undefined : onClose}
      footer={
        <>
          <button type="button" className="btn-admin-secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? 'btn-admin-danger' : 'btn-admin'}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Procesando…' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm text-muted">{message}</p>
    </Modal>
  );
}

export function FormField({ id, label, type = 'text', value, onChange, required, disabled, placeholder, autoComplete }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
        {required ? ' *' : ''}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        className="field-input"
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
    </div>
  );
}

export function TextAreaField({ id, label, value, onChange, required, disabled, rows = 4 }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
        {required ? ' *' : ''}
      </label>
      <textarea
        id={id}
        name={id}
        className="field-textarea"
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        rows={rows}
      />
    </div>
  );
}

export function SelectField({ id, label, value, onChange, options, required, disabled, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
        {required ? ' *' : ''}
      </label>
      <select
        id={id}
        name={id}
        className="field-input"
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function FileField({ id, label, onChange, accept, disabled, hint }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <input
        id={id}
        name={id}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={onChange}
        className="block w-full text-sm text-muted file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-primary-dark"
      />
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </div>
  );
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b border-line pb-px" role="tablist">
      {tabs.map((tab, index) => (
        <button
          key={tab.id || tab.label}
          type="button"
          role="tab"
          aria-selected={value === index}
          className={`min-h-10 shrink-0 cursor-pointer rounded-t-md px-4 text-sm font-semibold transition-colors ${
            value === index
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted hover:text-ink'
          }`}
          onClick={() => onChange(index)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function DataTable({ columns, rows, rowKey = 'id', emptyTitle = 'Sin datos' }) {
  if (!rows?.length) {
    return <EmptyState title={emptyTitle} />;
  }

  return (
    <>
      <div className="hidden overflow-x-auto rounded-lg border border-line bg-surface md:block">
        <table className="table-admin">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} className={col.className}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row[rowKey]} className="hover:bg-canvas/60">
                {columns.map((col) => (
                  <td key={col.key} className={col.className}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="space-y-3 md:hidden">
        {rows.map((row) => (
          <li key={row[rowKey]} className="rounded-lg border border-line bg-surface p-4">
            {columns.map((col) => (
              <div key={col.key} className="flex justify-between gap-3 border-b border-line py-2 last:border-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted">{col.header}</span>
                <span className="text-right text-sm text-ink">
                  {col.render ? col.render(row) : row[col.key]}
                </span>
              </div>
            ))}
          </li>
        ))}
      </ul>
    </>
  );
}
